const ShipsyAdapter = require('./data-access/adapter')
const { getServiceForLocationId } = require('../service/tools/territory-lookup')
const { SHIPMENT_TYPE_ALL_TYPES } = require('../shipment')
const getPeriodFromProgram = require('../report/tools/get-period-from-program')
const createReport = require('../report/tools/create')
const { bulkUpsert } = require('../report/data-access/pouchdb-adapter')
const { safeDateParser, fsShipmentDateParser } = require('../shipment/tools/safe-date-parser')
const {
  batchTrackingFromUser,
  buildSnapshotBase,
  joinRemarks,
  keyUnknownBatch,
  keySkuSupplier,
  keySkuBatch,
  qtyRtv,
  qtySalesReturn,
  sumCounts,
  swapOriginDest,
  aggregateQuantitiesBySku,
  updateCountsFromSkuQuantities
} = require('./utils')

class ShipsyApi {
  constructor (shipsyApiConfigs = {}, isomorphicFetch, agaveAdapter, logger, user, pgConnection, mainApi) {
    this.fetch = isomorphicFetch
    this.agaveAdapter = agaveAdapter
    this.pgConnection = pgConnection
    this.logger = logger
    this.user = user
    this.mainApi = mainApi
    this.shipsyAdapter = new ShipsyAdapter(shipsyApiConfigs, this.fetch, logger, mainApi)
  }

  /**
   * Create a Return-To-Vendor (RTV) snapshot for given params.
   *
   * @param {Object} params - RTV request payload
   * @example See ./example_payloads/return-to-vendor.json for a complete sample structure
   */
  async returnToSupplier (params) {
    const {
      po_reference: POReference // request's PO reference, used to look up original shipment
    } = params

    const trackBatches = batchTrackingFromUser(this.user)

    const { shipments } = await this.mainApi.shipment.listLastMileDeliveries({
      deliveryType: SHIPMENT_TYPE_ALL_TYPES,
      filters: {shipment_number: POReference}
    })

    const [originalShipment] = shipments // We expect just 1 shipment here as we used a unique id to filter

    if (!originalShipment) {
      const err = new Error(`returnToSupplier: shipment not found for PO reference: ${POReference}`)
      err.status = 404
      throw err
    }

    if (!Array.isArray(params.items)) {
      const err = new Error('returnToSupplier: items missing')
      err.status = 400
      throw err
    }

    // Idempotency: do not create an RTV snapshot if one already exists for this GRN reference with same status.
    // We consider an RTV snapshot to be a received snapshot linked to this orderId.
    try {
      const count = await this.mainApi.shipment.listLastMileDeliveries({
        filters: { shipment_id: originalShipment.id, status: ['received'] },
        onlyCount: true,
        addSnapshotsData: false,
        deliveryType: SHIPMENT_TYPE_ALL_TYPES
      })
      if (count && count > 0) {
        return
      }
    } catch (_) {
      this.logger.warn({ code: 'rtv-snapshot-already-exists', orderId: originalShipment.orderId, POReference })
    }

    const unifiedRemarks = joinRemarks(params.items.map(item => item.grn_json_data && item.grn_json_data.grn_remarks))

    const counts = sumCounts(params.items, {
      nested: false,
      key: (it) => (trackBatches ? keyUnknownBatch(it.sku_code) : keySkuSupplier(it.sku_code, it.supplier_reference)),
      qty: qtyRtv,
      enforceNonNull: true
    })
    // Normalize grn_date to YYYY-MM-DD format to prevent bad dates in snapshot IDs
    const normalizedGrnDate = params.grn_date ? fsShipmentDateParser(params.grn_date) : params.grn_date
    const snapshotData = {
      ...buildSnapshotBase(originalShipment, normalizedGrnDate, unifiedRemarks),
      counts,
      route: { id: originalShipment.route.id },
      planningType: originalShipment.planningType
    }
    return this.mainApi.shipment.createSnapshot(snapshotData) // TODO: Create in postgresql, then rely on a sync to couchdb (discuss as RFC)
  }
  async inventoryAdjustment (params) {
    if (!params) {
      const err = new Error('inventoryAdjustment: payload is required')
      err.status = 400
      throw err
    }

    const singleAdjustment = Array.isArray(params.inventory_adjustment_data) ? params.inventory_adjustment_data[0] : null

    const warehouseCode = singleAdjustment && singleAdjustment.warehouse

    if (!singleAdjustment || !warehouseCode) {
      const err = new Error('inventoryAdjustment: warehouse and adjustment payload are required')
      err.status = 400
      throw err
    }

    if (!singleAdjustment.sku_code) {
      const err = new Error('inventoryAdjustment: sku_code is required')
      err.status = 400
      throw err
    }

    const updatedQuantityValue = singleAdjustment.updated_quantity

    const updatedQuantity = Number(updatedQuantityValue)
    if (!Number.isFinite(updatedQuantity)) {
      const err = new Error('inventoryAdjustment: updated_quantity is required')
      err.status = 400
      throw err
    }

    const location = await this.mainApi.location.pgAdapter.getOneByCode(warehouseCode)
    const locationId = location.fsid

    const territory = getServiceForLocationId(locationId)
    const serviceId = typeof territory === 'string' ? territory : (territory && (territory.service || territory.id))

    if (!serviceId) {
      if (this.logger && this.logger.warn) {
        this.logger.warn('inventoryAdjustment: skipping adjustment due to missing service', {
          warehouse: warehouseCode,
          locationId,
          sku_code: singleAdjustment.sku_code
        })
      }
      return
    }

    const service = await this.mainApi.service.get(serviceId)

    let ledger
    try {
      ledger = await this.mainApi.stock.getLedgerBalanceAsReport({
        locationId,
        service,
        excludeProducts: 'configuration',
        calculateFields: true,
        usePGLedger: true
      })
    } catch (err) {
      throw new Error(`inventoryAdjustment: unable to load ledger for ${locationId}: ${err.message || err}`)
    }

    if (!ledger || !ledger.stock) {
      throw new Error('inventoryAdjustment: ledger did not include stock data')
    }

    const productKey = `product:${singleAdjustment.sku_code}`
    const stockFromLedger = ledger.stock || {}
    const clonedStock = Object.keys(stockFromLedger).reduce((acc, key) => {
      const product = stockFromLedger[key] || {}
      acc[key] = {
        ...product,
        fields: { ...(product.fields || {}) }
      }
      if (product.batches) {
        acc[key].batches = { ...product.batches }
      }
      if (key !== productKey) {
        acc[key].autoFilled = true
      } else {
        delete acc[key].autoFilled
      }
      return acc
    }, {})

    if (!clonedStock[productKey]) {
      clonedStock[productKey] = { fields: {} }
    }
    clonedStock[productKey].fields = clonedStock[productKey].fields || {}
    clonedStock[productKey].fields['field:standard-physical-count'] = { amount: updatedQuantity }
    clonedStock[productKey].fields['field:shelflife-balance'] = { amount: updatedQuantity }

    let stock = clonedStock

    const now = new Date().toISOString()
    const referenceDate = safeDateParser(singleAdjustment.adjusted_at || singleAdjustment.created_at) || now
    const period = getPeriodFromProgram(service.program, referenceDate, true)
    const username = singleAdjustment.adjusted_by ||
      singleAdjustment.created_by ||
      (this.user && this.user.name) ||
      'shipsy-automation'

    const stockCountDoc = createReport({
      locationId,
      service,
      period,
      username,
      createdAt: now,
      submittedAt: now,
      stock,
      partialCount: true
    })

    stockCountDoc.documents = ledger.documents || {}
    stockCountDoc.partialCount = true

    await bulkUpsert(this.mainApi.state, [stockCountDoc])

    if (this.logger && this.logger.info) {
      this.logger.info('inventoryAdjustment: created partial count from Shipsy payload', {
        warehouse: warehouseCode,
        sku_code: singleAdjustment.sku_code,
        updatedQuantity,
        reportId: stockCountDoc._id
      })
    }
  }

  async grnCreation (params) {
    // Create shipment received snapshot - always set to 'received' status
    if (!params || !Array.isArray(params.items) || params.items.length === 0) {
      const err = new Error('grnCreation: po_reference parameter is required')
      err.status = 400
      throw err
    }
    const shipmentId = params.items[0].po_reference
    if (!shipmentId) {
      const err = new Error('grnCreation: po_reference parameter is required')
      err.status = 400
      throw err
    }
    let shipment
    try {
      shipment = await this.mainApi.shipment.getShipmentByNumber({ shipmentNumber: shipmentId })
    } catch (error) {
      const err = new Error(`grnCreation: shipment not found for PO reference: ${shipmentId}: ${error}`)
      err.status = 404
      throw err
    }
    if (!shipment) {
      const err = new Error(`grnCreation: shipment not found for PO reference: ${shipmentId}`)
      err.status = 404
      throw err
    }
    // Idempotency: check in Postgres if a 'received' shipment exists for this orderId
    try {
      const count = await this.mainApi.shipment.listLastMileDeliveries({
        filters: { shipment_id: shipment.id, status: ['received'] },
        onlyCount: true,
        addSnapshotsData: false,
        deliveryType: SHIPMENT_TYPE_ALL_TYPES
      })
      if (count && count > 0) {
        return
      }
    } catch (_) {
      this.logger.warn({code: 'received-snapshot-already-exists', orderId: shipment.orderId})
    }

    // Align shipment counts with the quantities confirmed on the GRN payload
    const grnQuantitiesBySku = aggregateQuantitiesBySku(params.items, item => item && item.grn_quantity)
    const countsWithGrnQuantities = updateCountsFromSkuQuantities(shipment.counts, grnQuantitiesBySku)

    // Create a new snapshot with 'received' status regardless of current status
    const snapshotData = {
      ...shipment,
      counts: countsWithGrnQuantities,
      doCheckSent: false,
      status: 'received'
    }

    await this.mainApi.shipment.createSnapshot(snapshotData)
  }

  async invoiceCallback (params) {
    if (!params) {
      const err = new Error('invoiceCallback: order_reference parameter is required')
      err.status = 400
      throw err
    }

    const orders = Array.isArray(params[0].orders) ? params[0].orders : []
    const orderReference = orders[0] && orders[0].order_reference

    if (!orderReference) {
      const err = new Error('invoiceCallback: order_reference parameter is required')
      err.status = 400
      throw err
    }

    const shipmentId = orderReference
    let shipment
    try {
      shipment = await this.mainApi.shipment.getShipmentByNumber({ shipmentNumber: shipmentId })
    } catch (err) {
      const e = new Error(`invoiceCallback: shipment not found for order reference: ${shipmentId}: ${err}`)
      e.status = 404
      throw e
    }
    if (!shipment) {
      const err = new Error(`invoiceCallback: shipment not found for order reference: ${shipmentId}`)
      err.status = 404
      throw err
    }

    const invoiceItems = orders.reduce((acc, order) => {
      if (order && Array.isArray(order.items)) {
        acc.push(...order.items)
      }
      return acc
    }, [])

    // Idempotency: check in Postgres if a 'sent' shipment exists for this orderId
    try {
      const count = await this.mainApi.shipment.listLastMileDeliveries({
        filters: { shipment_id: shipment.id, status: ['sent'] },
        onlyCount: true,
        addSnapshotsData: false,
        deliveryType: SHIPMENT_TYPE_ALL_TYPES
      })
      if (count && count > 0) {
        return
      }
    } catch (_) {
      this.logger.warn({code: 'sent-snapshot-already-exists', orderId: shipment.orderId})
    }

    const invoiceQuantitiesBySku = aggregateQuantitiesBySku(invoiceItems, item => item && item.invoice_quantity)
    const countsWithInvoiceQuantities = updateCountsFromSkuQuantities(shipment.counts, invoiceQuantitiesBySku)

    // Create a new snapshot with 'sent' status regardless of current status
    const snapshotData = {
      ...shipment,
      counts: countsWithInvoiceQuantities,
      doCheckSent: false,
      status: 'sent'
    }

    await this.mainApi.shipment.createSnapshot(snapshotData) // Shipment sent from client's facility
  }

  async salesReturn (params) {
    if (!params) {
      const err = new Error('salesReturn: params parameter is required')
      err.status = 400
      throw err
    }
    const payload = Array.isArray(params) ? params[0] : params
    if (typeof payload.reference_number === 'undefined') {
      const err = new Error('salesReturn: reference_number parameter is required')
      err.status = 400
      throw err
    }
    let originalShipment
    try {
      originalShipment = await this.mainApi.shipment.getShipmentByNumber({ shipmentNumber: payload.reference_number })
    } catch (err) {
      const error = new Error(`Shipment not found: ${payload.reference_number}: ${err}`)
      error.status = 404
      throw error
    }
    if (!originalShipment) {
      const err = new Error(`Shipment not found: ${payload.reference_number}`)
      err.status = 404
      throw err
    }

    const entryItems = (payload.items && payload.items[0] && payload.items[0].items) || []
    const unifiedRemarks = joinRemarks(entryItems.map(item => item.remarks))

    // Aggregate counts for returned items
    const counts = sumCounts(payload.items, {
      nested: true,
      key: (it) => keySkuBatch(it.sku_description, it.batch_number),
      qty: qtySalesReturn
    })
    // Idempotency: check in Postgres if a 'received' shipment exists for this orderId
    try {
      const count = await this.mainApi.shipment.listLastMileDeliveries({
        filters: { shipment_id: originalShipment.id, status: ['received'] },
        onlyCount: true,
        addSnapshotsData: false,
        deliveryType: SHIPMENT_TYPE_ALL_TYPES
      })
      if (count && count > 0) {
        return
      }
    } catch (_) {
      this.logger.warn({code: 'received-snapshot-already-exists', orderId: originalShipment.orderId})
    }

    const snapshotData = {
      ...originalShipment,
      ...swapOriginDest(originalShipment),
      doCheckSent: false,
      counts,
      comment: unifiedRemarks,
      status: 'received'
    }

    console.log('Creating sales return snapshot with data:', {
      orderId: snapshotData.orderId,
      status: snapshotData.status,
      comment: snapshotData.comment,
      countsKeys: Object.keys(snapshotData.counts || {})
    })
    await this.mainApi.shipment.createSnapshot(snapshotData)
  }
}

module.exports = ShipsyApi
